import { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Phone } from 'lucide-react';
import axios from 'axios';
import { pushFormSubmitEvent } from '../lib/gtm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_type: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    'Basement Development',
    'Bathroom Renovation',
    'Legal Basement Suite',
    'Custom Features',
    'Flooring Installation',
    'Other',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        // Push form_submit event to dataLayer for Google Ads enhanced conversions (only on success)
        pushFormSubmitEvent(formData.email, formData.phone);
        
        setStatus({
          type: 'success',
          message: response.data.message || 'Thank you! Your message has been sent. We\'ll be in touch within 24 hours.',
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          project_type: '',
          message: '',
        });
      } else {
        // Email failed but request succeeded
        setStatus({
          type: 'error',
          message: response.data.message || 'There was an issue sending your message. Please call us directly.',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Something went wrong. Please try again or call us directly.';
      setStatus({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      {/* Status Message */}
      {status.message && (
        <div
          className={`p-4 flex items-start gap-3 ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
          data-testid="form-status-message"
        >
          {status.type === 'success' ? (
            <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          )}
          <div>
            <span>{status.message}</span>
            {status.type === 'error' && (
              <div className="mt-2">
                <a 
                  href="tel:403-402-6596" 
                  className="inline-flex items-center gap-2 text-red-700 font-medium hover:text-red-900"
                >
                  <Phone size={16} />
                  Call us: 403-402-6596
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Name & Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-[#0f172a] mb-2">
            Your Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="John Smith"
            data-testid="contact-input-name"
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-[#0f172a] mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="(403) 555-1234"
            data-testid="contact-input-phone"
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-[#0f172a] mb-2">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="form-input"
          placeholder="john@example.com"
          data-testid="contact-input-email"
        />
      </div>

      {/* Project Type */}
      <div>
        <label htmlFor="project_type" className="block text-sm font-medium text-[#0f172a] mb-2">
          Project Type *
        </label>
        <select
          id="project_type"
          name="project_type"
          value={formData.project_type}
          onChange={handleChange}
          required
          className="form-input cursor-pointer"
          data-testid="contact-input-project-type"
        >
          <option value="">Select a project type</option>
          {projectTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-[#0f172a] mb-2">
          Tell Us About Your Project *
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="form-input resize-none"
          placeholder="Please describe your project, including any specific features or requirements..."
          data-testid="contact-input-message"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="contact-submit-button"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send size={18} />
            Send Message
          </>
        )}
      </button>

      <p className="text-sm text-slate-500 text-center">
        We typically respond within 24 hours. For urgent inquiries, call us at{' '}
        <a href="tel:403-402-6596" className="text-[#0f172a] font-medium hover:text-[#c5a059]">
          403-402-6596
        </a>
      </p>
    </form>
  );
};

export default ContactForm;
